import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { FC, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, useHistory, withRouter } from "react-router-dom";

import { Loader } from "@livechat/design-system";
import { DesignToken, RadiusToken, SpacingToken } from "@livechat/design-system-react-components";

import { BackButton, ViewContainer } from "components/grid";
import Block from "components/layout/Block";
import { PaymentFailedAlert, PaymentFailedLocation } from "components/payment-failures-limits/PaymentFailedAlert";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { EventPlace, trackEngagement, trackGAEvent } from "helpers/analytics";
import { usePaymentFailure } from "hooks/use-payment-failure";
import { actions$ } from "store/action-stream";
import { getBilling, getIsFetchingBilling } from "store/entities/billing/selectors";
import { CreditCardActionNames, CreditCardActions } from "store/entities/credit-card/actions";
import { IAddCreditCardRequestPayload } from "store/entities/credit-card/interfaces";
import { ISubscription } from "store/entities/subscriptions";
import { SUBSCRIPTION, SubscriptionActions } from "store/entities/subscriptions/actions";
import { IAction } from "store/helper";
import { getIsSavingCreditCard } from "store/views/credit-card/selectors";
import { withPx, withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";

import { SubscriptionCardForm } from "../components/SubscriptionCardForm/SubscriptionCardForm";
import { SubscriptionRoutes } from "../constants";
import { TaxDetailsProvider } from "../contexts/tax-details-context";
import { ContentBoxWrapper } from "../styled";

import { SubscriptionCardInfo } from "./SubscriptionCardInfo";
import { PricingSummary } from "./pricing-summary";

interface IProps extends RouteComponentProps {
  newSubscription: ISubscription;
}

const SubscriptionCardContainer: FC<IProps> = ({ newSubscription }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const subscriptionCardSubmitButtonRef = useRef<HTMLButtonElement>(null);
  const { failed: paymentFailed } = usePaymentFailure();

  const isFetchingBilling = useSelector(getIsFetchingBilling);
  const billing = useSelector(getBilling);
  const isSavingCreditCard = useSelector(getIsSavingCreditCard);

  const hasBillingInfo = !!billing?.billingInfo;

  const handleCreditCardSave = (creditCardData: IAddCreditCardRequestPayload): void => {
    dispatch(CreditCardActions.addCreditCard(creditCardData));
  };

  const handleSubscriptionSave = (subscription: Partial<ISubscription>): void => {
    dispatch(SubscriptionActions.create({ value: subscription }));
  };

  const handleSaveOnlySubscription = (): void => {
    handleSubscriptionSave(newSubscription);
  };

  const handleSubmitCardForm = (e: React.MouseEvent): void => {
    e.preventDefault();
    trackEngagement("First payment - subscribed", EventPlace.Subscription);
    trackEngagement("Subscription: Payment form Subscribe now button clicked", EventPlace.Subscription);

    if (hasBillingInfo) {
      handleSaveOnlySubscription();
    } else {
      subscriptionCardSubmitButtonRef.current?.click();
    }
  };

  const handleSubmitCardFormSuccess = (tokenId: string): void => {
    handleCreditCardSave({
      token: tokenId,
      newSubscription,
    });
  };

  useEffect(() => {
    trackGAEvent("global.require", "ecommerce");
    trackEngagement("First payment - form displayed", EventPlace.Subscription);

    const unsubscribe = pipe(
      actions$,
      filter(
        (action: IAction) =>
          action.type === SUBSCRIPTION.UPDATE.SUCCESS ||
          action.type === SUBSCRIPTION.CREATE.SUCCESS ||
          action.type === CreditCardActionNames.ADD_CREDIT_CARD_SUCCESS
      ),
      subscribe(() => {
        history.push(SubscriptionRoutes.ThankYou);
      })
    );

    return unsubscribe;
  }, []);

  return (
    <TaxDetailsProvider defaultCountryCode={billing?.billingInfo?.country} defaultZipCode={billing?.billingInfo?.zip}>
      <ThemeProvider theme={legacyTheme}>
        <ViewContainer
          title="Credit card"
          leftNodes={<BackButton onClick={() => history.push(SubscriptionRoutes.Plans)}>Back</BackButton>}
          placement="main-content"
        >
          <ContentBoxWrapper>
            <StyledWrapper>
              <StyledContainer>
                {isFetchingBilling ? (
                  <Loader size="large" />
                ) : (
                  <>
                    <BillingInfoWrapper>
                      {hasBillingInfo ? (
                        <SubscriptionCardInfo billing={billing} />
                      ) : (
                        <SubscriptionCardForm
                          ref={subscriptionCardSubmitButtonRef}
                          billing={billing}
                          onSubmitSuccess={handleSubmitCardFormSuccess}
                          isSaving={isSavingCreditCard}
                          expandable
                        />
                      )}
                      {paymentFailed && (
                        <Block marginTop={withVar(SpacingToken.Spacing8)}>
                          <PaymentFailedAlert location={PaymentFailedLocation.SubUpdate} />
                        </Block>
                      )}
                    </BillingInfoWrapper>
                    <SummaryWrapper>
                      <PricingSummary
                        newSubscription={newSubscription}
                        isSavingCreditCard={isSavingCreditCard}
                        onSubscribe={handleSubmitCardForm}
                      />
                    </SummaryWrapper>
                  </>
                )}
              </StyledContainer>
            </StyledWrapper>
          </ContentBoxWrapper>
        </ViewContainer>
      </ThemeProvider>
    </TaxDetailsProvider>
  );
};

const StyledWrapper = styled.div`
  width: 750px;
  padding: ${withVar(SpacingToken.Spacing16)};
  @media (max-width: ${withPx(ResponsiveBreakpoint.Mobile)}) {
    padding: 0;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  border: 1px solid ${withVar(DesignToken.BorderBasicPrimary)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  padding: 40px 60px 45px 40px;
  font-size: 14px;
  color: ${withVar(DesignToken.ContentBasicPrimary)};

  @media (max-width: ${withPx(ResponsiveBreakpoint.Mobile)}) {
    flex-direction: column;
    border: 0;
    padding: ${withVar(SpacingToken.Spacing6)} ${withVar(SpacingToken.Spacing5)};
  }
`;

const BillingInfoWrapper = styled.div`
  width: 100%;
  flex: 2;
  margin-right: 36px;

  @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
    margin-right: 0;
    margin-bottom: 30px;
  }
`;

const SummaryWrapper = styled.div`
  width: 100%;
  flex: 1;
`;

export default withRouter(SubscriptionCardContainer);
